import { useClerk } from '@clerk/clerk-react'
import { useSearchParams } from 'react-router-dom'
import { entryPathFor, setEntryContext } from '../lib/entryContext'

export default function WelcomePage() {
  const { redirectToSignIn } = useClerk()
  const [searchParams] = useSearchParams()
  // The page a signed-out link asked for. entryPathFor checks it, so an unsafe
  // or missing value simply sends Log in to the plain entry picker.
  const entryPath = entryPathFor(searchParams.get('next') ?? '')

  const signIn = (returnTo: string) => {
    if (returnTo.startsWith('/onboarding/')) {
      setEntryContext({ previewRole: 'Merchant' })
    }
    // Clerk v5 deprecated the bare `redirectUrl` option (Sentry DELIVERYAPPS-10). `force` matches
    // the old behaviour: always land on the target the clicked button asked for. Sign-up is set too
    // because Clerk's hosted sign-in offers a "create account" path out of the same flow.
    const target = window.location.origin + returnTo
    void redirectToSignIn({
      signInForceRedirectUrl: target,
      signUpForceRedirectUrl: target
    })
  }

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-tr from-accent to-accent-strong flex items-center justify-center shadow-md">
            <span className="text-white font-bold text-sm">CH</span>
          </div>
          <span className="font-display font-bold text-lg tracking-tight">
            Comfort<span className="text-accent">Hub</span>
          </span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 md:py-12 space-y-8">
        <div className="space-y-3">
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">
            Merchant backoffice
          </h1>
          <p className="text-sm text-foreground-muted leading-relaxed">
            Manage stores, catalog, orders, and scheduling for your organization on the ComfortHub
            platform. Sign in to continue with an existing account, or apply to join as a new
            merchant.
          </p>
        </div>

        {/* Most people who land here already have an account, so signing in comes first and on its own. */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-sm font-medium">Already with ComfortHub?</p>
          <button
            type="button"
            onClick={() => signIn(entryPath)}
            className="inline-flex items-center justify-center rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold text-white hover:bg-accent-strong transition-colors"
          >
            Log in
          </button>
        </div>

        <section
          aria-labelledby="welcome-apply-heading"
          className="rounded-2xl border border-border bg-surface p-6 space-y-4"
        >
          <h2 id="welcome-apply-heading" className="text-lg font-semibold">Apply to join ComfortHub</h2>
          <p className="text-sm text-foreground-muted">
            Create or sign in to your ComfortHub account, then submit your organization for platform
            review. After approval you can complete setup and start operating.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={() => signIn('/onboarding/apply')}
              className="inline-flex items-center justify-center rounded-xl border border-border px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-surface-hover transition-colors"
            >
              Apply to join
            </button>
          </div>
        </section>
      </main>
    </div>
  )
}
